import type {
  AmortizationInput,
  Bereich,
  BillInput,
  Category,
  ClearDoneShoppingInput,
  CreateCategoryInput,
  CreatePaymentInput,
  CreateShoppingItemInput,
  CreateShoppingSectionInput,
  CreateUserInput,
  DocumentDetail,
  DocumentStatus,
  Donation,
  Health,
  HypothekDaten,
  HypothekImportInput,
  HypothekImportResult,
  ImportResult,
  IncomeEntry,
  LoanInput,
  LoginInput,
  ManagedDocument,
  MortgageSettings,
  Note,
  PreviewInfo,
  PublicUser,
  ReorderNotesInput,
  RestoreShoppingItemsInput,
  RichFile,
  SaveMonthInput,
  SaveQuarterInput,
  SaveTaxesInput,
  SetupInput,
  ShoppingItem,
  ShoppingSection,
  TaxEntry,
  TaxYearFigures,
  TexterkennungAuftrag,
  UpdateCategoryInput,
  UpdateDocumentInput,
  UpdateShoppingItemInput,
  UpdateShoppingSectionInput,
  UpsertNoteInput,
  UtilityBill,
  YearFigures,
} from '@manager/shared'

import { meldungUnlesbar, unlesbareDatei } from './einlesen'
import { istWegFehler, meldeVerbindung } from './offline/verbindung'

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080'

/** Für Adressen, die der Browser direkt lädt (Bilder, Downloads). */
export const API_BASE = API_URL

/**
 * Trägt Code und Feldfehler der API mit – damit kann ein Formular die Meldung
 * direkt am richtigen Eingabefeld anzeigen, statt alles in einen Balken oben zu kippen.
 */
export class ApiRequestError extends Error {
  readonly code: string
  readonly status: number
  readonly fields: Record<string, string>
  /**
   * Die ganze Antwort des Servers. Manche Fehler bringen mehr mit als eine
   * Meldung – ein Duplikat etwa das Dokument, das schon in der Ablage liegt.
   */
  readonly data: unknown

  constructor(
    status: number,
    code: string,
    message: string,
    fields?: Record<string, string>,
    data?: unknown,
  ) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = status
    this.code = code
    this.fields = fields ?? {}
    this.data = data
  }
}

/**
 * Was beim Hochladen schon festgelegt werden kann.
 *
 * Alles freiwillig: Ohne Angabe entsteht der Titel aus dem Dateinamen, und das
 * Dokument liegt unsortiert, für beide und pendent in der Ablage. Leere Werte
 * werden nicht mitgeschickt – „keine Kategorie" ist beim Hochladen kein Wunsch,
 * sondern der Normalfall.
 */
export interface UploadDetails {
  title?: string
  /** In welche Sammlung das Dokument gehört. Ohne Angabe der Haushalt. */
  bereich?: Bereich
  categoryId?: string | null
  assignedTo?: string | null
  status?: DocumentStatus
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${API_URL}${path}`, {
      ...init,
      // Ohne 'include' schickt der Browser das Sitzungs-Cookie nicht an
      // manager-api.alae.app – die häufigste Ursache für 'plötzlich abgemeldet'.
      credentials: 'include',
      headers: {
        // Nur bei JSON selbst setzen. Bei FormData muss der Browser den
        // content-type samt multipart-Grenze bestimmen – setzt man ihn hier,
        // kann der Server den Datenstrom nicht mehr zerlegen.
        ...(typeof init.body === 'string' ? { 'content-type': 'application/json' } : {}),
        ...init.headers,
      },
    })
  } catch (error) {
    // Selbst abgebrochen – das sagt nichts über die Verbindung.
    if (init.signal?.aborted) throw error
    // Bricht ein Hochladen ohne Antwort ab, kann auch die Datei schuld sein:
    // Chrome auf Android kommt an manche Dateien aus Google Drive nicht heran
    // und bricht die Anfrage dann ab wie bei fehlender Verbindung (siehe
    // `einlesen.ts`).
    const unlesbar = init.body instanceof FormData ? await unlesbareDatei(init.body) : null
    if (unlesbar) {
      throw new ApiRequestError(0, 'file_unreadable', meldungUnlesbar(unlesbar.name))
    }
    meldeVerbindung(false)
    throw new ApiRequestError(
      0,
      'network_error',
      'Keine Verbindung zum Server. Bist du offline?',
    )
  }

  // Eine Antwort, die nicht vom Server stammt, sondern vom Weg dorthin – der
  // Cloudflare-Tunnel ohne Gegenstelle etwa. Für die App ist das dasselbe
  // wie keine Verbindung (siehe `offline/verbindung.ts`).
  meldeVerbindung(!istWegFehler(response.status))

  if (response.status === 204) return undefined as T

  const text = await response.text()
  // Eine Fehlerseite aus HTML ist kein Grund für einen Absturz beim Lesen;
  // dann gilt eben die allgemeine Meldung.
  let payload: unknown = null
  try {
    payload = text ? JSON.parse(text) : null
  } catch {
    if (response.ok) {
      throw new ApiRequestError(response.status, 'invalid_response', 'Unerwartete Antwort.')
    }
  }

  if (!response.ok) {
    const error =
      payload && typeof payload === 'object' && 'error' in payload
        ? (payload as { error: { code: string; message: string; fields?: Record<string, string> } })
            .error
        : null

    throw new ApiRequestError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? 'Unerwarteter Fehler.',
      error?.fields,
      payload,
    )
  }

  return payload as T
}

/**
 * Holt eine Datei als Blob – über denselben Weg wie jeder andere Aufruf.
 *
 * Nicht der Umweg, für den es aussieht: Ein <img src="https://manager-api…">
 * ist für den Browser eine Anfrage an eine fremde Adresse. Die
 * Sicherheitsrichtlinie der Seite (img-src) erlaubt aber nur die eigene
 * Herkunft – das Bild wurde stumm verworfen, und die Vorschau blieb leer.
 * Über fetch() greift stattdessen connect-src, wo die API bereits eingetragen
 * ist; aus dem Blob wird danach eine blob:-Adresse, die jede Richtlinie
 * durchlässt. Nebenbei ist so sichergestellt, dass das Sitzungs-Cookie
 * mitgeht, egal wo die API einmal liegt.
 */
async function requestBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  let response: Response
  try {
    response = await fetch(`${API_URL}${path}`, { credentials: 'include', signal })
  } catch {
    throw new ApiRequestError(0, 'network_error', 'Keine Verbindung zum Server. Bist du offline?')
  }

  if (!response.ok) {
    // Der Fehlerkörper ist hier JSON, obwohl ein Bild erwartet wurde.
    let code = 'unknown'
    let message = 'Die Datei konnte nicht geladen werden.'
    try {
      const payload: unknown = await response.json()
      if (payload && typeof payload === 'object' && 'error' in payload) {
        const error = (payload as { error: { code: string; message: string } }).error
        code = error.code
        message = error.message
      }
    } catch {
      // Kein JSON – dann bleibt es bei der allgemeinen Meldung.
    }
    throw new ApiRequestError(response.status, code, message)
  }

  return response.blob()
}

export const api = {
  health: () => request<Health>('/api/health'),

  setupStatus: () => request<{ needsSetup: boolean }>('/api/setup/status'),

  setup: (input: SetupInput) =>
    request<{ user: PublicUser }>('/api/setup', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  login: (input: LoginInput) =>
    request<{ user: PublicUser }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),

  me: () => request<{ user: PublicUser }>('/api/auth/me'),

  listUsers: () => request<{ users: PublicUser[] }>('/api/users'),

  createUser: (input: CreateUserInput) =>
    request<{ user: PublicUser }>('/api/users', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  listCategories: (bereich: Bereich) =>
    request<{ categories: Category[] }>(`/api/categories?bereich=${bereich}`),

  createCategory: (input: CreateCategoryInput) =>
    request<{ category: Category }>('/api/categories', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  renameCategory: (id: string, input: UpdateCategoryInput) =>
    request<{ category: Category }>(`/api/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  /** Antwortet mit der Zahl der Dokumente, die dadurch unsortiert geworden sind. */
  deleteCategory: (id: string) =>
    request<{ unsorted: number }>(`/api/categories/${id}`, { method: 'DELETE' }),

  listDocuments: (query = '') =>
    request<{ documents: ManagedDocument[]; total: number }>(`/api/documents${query}`),

  getDocument: (id: string) => request<{ document: DocumentDetail }>(`/api/documents/${id}`),

  uploadDocument: (file: File, allowDuplicate = false, details: UploadDetails = {}) => {
    const body = new FormData()
    // Alle Felder müssen vor der Datei stehen: Der Server liest den Datenstrom
    // der Reihe nach und hat beim Empfang der Datei nur die Felder zur Hand,
    // die vorher kamen. Der Bereich zuerst – er entscheidet, in welcher Ablage
    // schon der Zwischenspeicher liegt.
    if (details.bereich) body.append('bereich', details.bereich)
    if (details.title) body.append('title', details.title)
    if (details.categoryId) body.append('categoryId', details.categoryId)
    if (details.assignedTo) body.append('assignedTo', details.assignedTo)
    if (details.status) body.append('status', details.status)
    body.append('file', file)
    // Kein content-type setzen: Der Browser muss die multipart-Grenze selbst
    // bestimmen, sonst kann der Server den Datenstrom nicht zerlegen.
    return request<{ document: ManagedDocument }>(
      `/api/documents${allowDuplicate ? '?allowDuplicate=1' : ''}`,
      { method: 'POST', body },
    )
  },

  updateDocument: (id: string, changes: UpdateDocumentInput) =>
    request<{ document: DocumentDetail }>(`/api/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    }),

  deleteDocument: (id: string) =>
    request<void>(`/api/documents/${id}`, { method: 'DELETE' }),

  restoreDocument: (id: string) =>
    request<{ document: ManagedDocument | null }>(`/api/documents/${id}/wiederherstellen`, {
      method: 'POST',
    }),

  retryOcr: (id: string) =>
    request<{ ocrStatus: string }>(`/api/documents/${id}/ocr`, { method: 'POST' }),

  /**
   * Ersetzt die Datei eines Dokuments – gebraucht vom nachträglichen
   * Zuschneiden. Titel, Datum und Kategorie bleiben, was sie waren.
   */
  replaceDocumentFile: (id: string, file: File) => {
    const body = new FormData()
    body.append('file', file)
    return request<{ document: DocumentDetail }>(`/api/documents/${id}/datei`, {
      method: 'PUT',
      body,
    })
  },

  documentPreview: (id: string) => request<PreviewInfo>(`/api/documents/${id}/preview`),

  /** Das kleine Bild für eine Kachel – bei PDFs die erste Seite. */
  documentThumbnail: (id: string, signal?: AbortSignal) =>
    requestBlob(`/api/documents/${id}/vorschaubild`, signal),

  documentPreviewPage: (id: string, page: number, signal?: AbortSignal) =>
    requestBlob(`/api/documents/${id}/preview/${page}`, signal),

  documentFile: (id: string, signal?: AbortSignal) =>
    requestBlob(`/api/documents/${id}/file`, signal),

  listShopping: () => request<{ items: ShoppingItem[] }>('/api/shopping'),

  addShoppingItem: (input: CreateShoppingItemInput) =>
    request<{ item: ShoppingItem }>('/api/shopping', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateShoppingItem: (id: string, changes: UpdateShoppingItemInput) =>
    request<{ item: ShoppingItem }>(`/api/shopping/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    }),

  deleteShoppingItem: (id: string) =>
    request<void>(`/api/shopping/${id}`, { method: 'DELETE' }),

  clearDoneShoppingItems: (input: ClearDoneShoppingInput = {}) =>
    request<{ removed: number }>('/api/shopping/erledigte-loeschen', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  restoreShoppingItems: (input: RestoreShoppingItemsInput) =>
    request<{ items: ShoppingItem[] }>('/api/shopping/wiederherstellen', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  listShoppingSections: () =>
    request<{ sections: ShoppingSection[] }>('/api/shopping/sections'),

  addShoppingSection: (input: CreateShoppingSectionInput) =>
    request<{ section: ShoppingSection }>('/api/shopping/sections', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  renameShoppingSection: (id: string, input: UpdateShoppingSectionInput) =>
    request<{ section: ShoppingSection }>(`/api/shopping/sections/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  reorderShoppingSections: (ids: string[]) =>
    request<{ sections: ShoppingSection[] }>('/api/shopping/sections/order', {
      method: 'PUT',
      body: JSON.stringify({ ids }),
    }),

  deleteShoppingSection: (id: string) =>
    request<{ moved: number; into: string }>(`/api/shopping/sections/${id}`, {
      method: 'DELETE',
    }),

  // Wie bei den Dokumenten kommt die fertige Abfrage von aussen – hier steht
  // nur, wohin sie geht.
  listNotes: (query = '') => request<{ notes: Note[] }>(`/api/notes${query}`),

  createNote: (note: UpsertNoteInput) =>
    request<{ note: Note }>('/api/notes', { method: 'POST', body: JSON.stringify(note) }),

  updateNote: (id: string, note: UpsertNoteInput) =>
    request<{ note: Note }>(`/api/notes/${id}`, { method: 'PATCH', body: JSON.stringify(note) }),

  deleteNote: (id: string) => request<void>(`/api/notes/${id}`, { method: 'DELETE' }),

  restoreNote: (id: string) =>
    request<{ note: Note }>(`/api/notes/${id}/wiederherstellen`, { method: 'POST' }),

  reorderNotes: (input: ReorderNotesInput) =>
    request<void>('/api/notes/reihenfolge', { method: 'PUT', body: JSON.stringify(input) }),

  /**
   * Eine Datei für eine Notiz – Bild oder PDF. Zurück kommt, was im Text der
   * Notiz steht: Kennung, Name, Typ, Grösse. Zur Notiz gehört sie, sobald die
   * Notiz mit ihr gespeichert ist.
   */
  uploadNoteFile: (file: File, bereich: Bereich, signal?: AbortSignal) => {
    const body = new FormData()
    // Der Bereich vor der Datei – wie beim Dokument entscheidet er, in
    // welcher Ablage sie liegt.
    body.append('bereich', bereich)
    body.append('file', file)
    // Mit Abbruchsignal: „Abbrechen" soll auch eine Datei anhalten, die gerade
    // unterwegs ist, statt sie fertig hochzuladen und dann liegen zu lassen.
    return request<{ file: RichFile }>('/api/notes/files', { method: 'POST', body, signal })
  },

  noteFilePreview: (id: string) => request<PreviewInfo>(`/api/notes/files/${id}/vorschau`),

  /** Das kleine Bild in der Notiz – bei einem PDF die erste Seite. */
  noteFileThumbnail: (id: string, signal?: AbortSignal) =>
    requestBlob(`/api/notes/files/${id}/vorschaubild`, signal),

  noteFilePreviewPage: (id: string, page: number, signal?: AbortSignal) =>
    requestBlob(`/api/notes/files/${id}/vorschau/${page}`, signal),

  noteFile: (id: string, signal?: AbortSignal) =>
    requestBlob(`/api/notes/files/${id}/datei`, signal),

  /** „Text erkennen": Datei hinschicken, Kennung zurück – siehe `texterkennen`. */
  startTexterkennung: (file: File, signal?: AbortSignal) => {
    const body = new FormData()
    body.append('file', file)
    return request<TexterkennungAuftrag>('/api/texterkennung', { method: 'POST', body, signal })
  },

  texterkennung: (id: string, signal?: AbortSignal) =>
    request<TexterkennungAuftrag>(`/api/texterkennung/${id}`, { signal }),

  getFinanceYear: (year: number) => request<FinanceYear>(`/api/finanzen/${year}`),

  saveTaxes: (year: number, input: SaveTaxesInput) =>
    request<FinanceYear>(`/api/finanzen/${year}/steuern`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  saveFinanceMonth: (year: number, month: number, input: SaveMonthInput) =>
    request<FinanceYear>(`/api/finanzen/${year}/monat/${month}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  addPayment: (year: number, payment: CreatePaymentInput) =>
    request<FinanceYear>(`/api/finanzen/${year}/zahlungen`, {
      method: 'POST',
      body: JSON.stringify(payment),
    }),

  deleteDonation: (year: number, id: string) =>
    request<FinanceYear>(`/api/finanzen/${year}/zahlungen/${id}`, { method: 'DELETE' }),

  listHausBills: () => request<HausBills>('/api/haus'),

  /**
   * Liest ein PDF aus, ohne etwas zu speichern. Was zurückkommt, füllt das
   * Formular der Vorschau – gespeichert wird erst mit `addHausBill`.
   */
  importHausBill: (file: File) => {
    const body = new FormData()
    body.append('file', file)
    // Kein content-type setzen: Der Browser muss die multipart-Grenze selbst
    // bestimmen, sonst kann der Server den Datenstrom nicht zerlegen.
    return request<ImportResult>('/api/haus/import', { method: 'POST', body })
  },

  /**
   * Übernimmt eine Rechnung – mit dem PDF, wenn eines dahintersteht.
   *
   * Die Datei geht im selben Aufruf mit, damit sie in derselben Bewegung in den
   * Dokumenten landet. Zwei getrennte Aufrufe hinterliessen bei einem Abbruch
   * entweder eine Rechnung ohne Beleg oder einen Beleg ohne Rechnung.
   */
  addHausBill: (bill: BillInput, file?: File | null, categoryId?: string | null) => {
    if (!file) {
      return request<HausBills>('/api/haus/rechnungen', {
        method: 'POST',
        body: JSON.stringify(bill),
      })
    }

    const body = new FormData()
    // Die Felder müssen vor der Datei stehen: Der Server liest den Datenstrom
    // der Reihe nach und hat beim Empfang der Datei nur, was vorher kam.
    body.append('bill', JSON.stringify(bill))
    if (categoryId) body.append('categoryId', categoryId)
    body.append('file', file)
    return request<HausBills>('/api/haus/rechnungen', { method: 'POST', body })
  },

  updateHausBill: (id: string, bill: BillInput) =>
    request<HausBills>(`/api/haus/rechnungen/${id}`, {
      method: 'PUT',
      body: JSON.stringify(bill),
    }),

  deleteHausBill: (id: string) =>
    request<HausBills>(`/api/haus/rechnungen/${id}`, { method: 'DELETE' }),

  getHypothek: () => request<HypothekDaten>('/api/haus/hypothek'),

  saveHypothekGrunddaten: (settings: MortgageSettings) =>
    request<HypothekDaten>('/api/haus/hypothek/grunddaten', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),

  addHypothekVertrag: (loan: LoanInput) =>
    request<HypothekDaten>('/api/haus/hypothek/vertraege', {
      method: 'POST',
      body: JSON.stringify(loan),
    }),

  updateHypothekVertrag: (id: string, loan: LoanInput) =>
    request<HypothekDaten>(`/api/haus/hypothek/vertraege/${id}`, {
      method: 'PUT',
      body: JSON.stringify(loan),
    }),

  deleteHypothekVertrag: (id: string) =>
    request<HypothekDaten>(`/api/haus/hypothek/vertraege/${id}`, { method: 'DELETE' }),

  saveHypothekQuartal: (year: number, quarter: number, input: SaveQuarterInput) =>
    request<HypothekDaten>(`/api/haus/hypothek/zinsen/${year}/${quarter}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  addAmortisation: (input: AmortizationInput) =>
    request<HypothekDaten>('/api/haus/hypothek/amortisation', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateAmortisation: (id: string, input: AmortizationInput) =>
    request<HypothekDaten>(`/api/haus/hypothek/amortisation/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  deleteAmortisation: (id: string) =>
    request<HypothekDaten>(`/api/haus/hypothek/amortisation/${id}`, { method: 'DELETE' }),

  /** Liest eine Excel-Tabelle aus, ohne etwas zu speichern – wie beim Rechnungsimport. */
  importHypothekExcel: (file: File) => {
    const body = new FormData()
    body.append('file', file)
    return request<HypothekImportResult>('/api/haus/hypothek/import', { method: 'POST', body })
  },

  uebernehmeHypothek: (daten: HypothekImportInput) =>
    request<HypothekDaten>('/api/haus/hypothek/uebernehmen', {
      method: 'POST',
      body: JSON.stringify(daten),
    }),
}

export interface HausBills {
  bills: UtilityBill[]
}

export interface FinanceYear {
  year: number
  entries: IncomeEntry[]
  donations: Donation[]
  /** Die Steuern dieses Jahres, etwa Bundessteuer und Staatssteuer. */
  taxEntries: TaxEntry[]
  /** Alle Steuerjahre mit dem, was darin noch offen ist – das älteste zuerst. */
  taxYears: TaxYearFigures[]
  figures: YearFigures
}
